import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip as LTooltip, Popup, useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { GridRegionImpact, GridMetricKey } from "../../../api/grid";
import type { GeneratorSummary } from "../../../api/generators";
import { STATUS_LABELS, isPlannedStatus } from "../../../api/generators";
import { getTechColor } from "../../generators/utils";
import MapCommandConsumer from "../../map-bridge/MapCommandConsumer";

// ─── Color scale ─────────────────────────────────────────────────────

/** Interpolate green → yellow → red based on t ∈ [0,1] */
function colorForValue(t: number): string {
  // Clamp
  const v = Math.max(0, Math.min(1, t));

  // Green (46,204,113) → Yellow (241,196,15) → Red (231,76,60)
  let r: number, g: number, b: number;
  if (v < 0.5) {
    const s = v * 2; // 0→1
    r = Math.round(46 + (241 - 46) * s);
    g = Math.round(204 + (196 - 204) * s);
    b = Math.round(113 + (15 - 113) * s);
  } else {
    const s = (v - 0.5) * 2; // 0→1
    r = Math.round(241 + (231 - 241) * s);
    g = Math.round(196 + (76 - 196) * s);
    b = Math.round(15 + (60 - 15) * s);
  }
  return `rgb(${r},${g},${b})`;
}

// ─── Pipeline overlay data ───────────────────────────────────────────

export interface PipelineBAData {
  count: number;
  capacity_mw: number;
  dalys_avoided: number;
  usd_avoided: number;
}

// ─── Props ───────────────────────────────────────────────────────────

interface GridMapProps {
  snapshotData: Map<string, GridRegionImpact>;
  selectedMetric: GridMetricKey;
  selectedBaCode: string | null;
  onZoneClick: (baCode: string) => void;
  onZoneHover?: (baCode: string | null) => void;
  pipelineByBA?: Map<string, PipelineBAData>;
  showPipelineOverlay?: boolean;
  generators?: GeneratorSummary[];
  showGeneratorOverlay?: boolean;
  /** When set, zoom/fit the map to this BA's GeoJSON boundary */
  focusBaCode?: string | null;
  /** GeoJSON URLs to load (defaults to US only) */
  geojsonUrls?: string[];
  /** Extract region code from a GeoJSON feature (defaults to US zoneName parsing) */
  codeExtractor?: (feature: GeoJSON.Feature) => string;
  /** Map center override */
  mapCenter?: [number, number];
  /** Map zoom override */
  mapZoom?: number;
}

// ─── Map updater (re-center) ─────────────────────────────────────────

function MapInvalidator({ version }: { version: number }) {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
  }, [map, version]);
  return null;
}

// ─── Custom pane for generators (renders above GeoJSON polygons) ─────

function GeneratorPane() {
  const map = useMap();
  useEffect(() => {
    if (!map.getPane("generatorPane")) {
      const pane = map.createPane("generatorPane");
      pane.style.zIndex = "450"; // above overlayPane (400), below markerPane (600)
    }
  }, [map]);
  return null;
}

// ─── Pipeline overlay (computes centroids from BA polygons) ─────────

function PipelineOverlay({
  geoData,
  pipelineByBA,
  extractBaCode,
  onZoneClick,
}: {
  geoData: GeoJSON.FeatureCollection;
  pipelineByBA: Map<string, PipelineBAData>;
  extractBaCode: (zoneNameOrFeature: string | GeoJSON.Feature) => string;
  onZoneClick: (baCode: string) => void;
}) {
  // Compute centroids from BA polygons
  const markers = useMemo(() => {
    const result: { baCode: string; lat: number; lng: number; data: PipelineBAData }[] = [];
    for (const feature of geoData.features) {
      const baCode = extractBaCode(feature);
      const pData = pipelineByBA.get(baCode);
      if (!pData) continue;

      // Compute centroid using a temporary Leaflet layer
      const tempLayer = L.geoJSON(feature as any);
      const center = tempLayer.getBounds().getCenter();
      result.push({ baCode, lat: center.lat, lng: center.lng, data: pData });
    }
    return result;
  }, [geoData, pipelineByBA, extractBaCode]);

  return (
    <>
      {markers.map((m) => (
        <CircleMarker
          key={m.baCode}
          center={[m.lat, m.lng]}
          radius={Math.max(5, Math.min(18, Math.log(m.data.capacity_mw + 1) * 3))}
          pathOptions={{
            fillColor: "#22c55e",
            color: "#ffffff",
            weight: 1.5,
            fillOpacity: 0.8,
            opacity: 0.9,
          }}
          eventHandlers={{ click: () => onZoneClick(m.baCode) }}
        >
          <LTooltip>
            <b>{m.baCode}</b><br />
            {m.data.count} planned generator{m.data.count !== 1 ? "s" : ""}<br />
            {m.data.capacity_mw.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW capacity<br />
            {m.data.dalys_avoided.toExponential(2)} DALYs avoided
          </LTooltip>
        </CircleMarker>
      ))}
    </>
  );
}

// ─── Generator overlay ──────────────────────────────────────────────

function GeneratorOverlay({ generators }: { generators: GeneratorSummary[] }) {
  const navigate = useNavigate();
  const mappable = useMemo(
    () => generators.filter((g) => g.latitude != null && g.longitude != null),
    [generators]
  );

  return (
    <>
      {mappable.map((g) => {
        const planned = isPlannedStatus(g.status);
        return (
          <CircleMarker
            key={`${g.plant_id}-${g.generator_id}`}
            center={[g.latitude!, g.longitude!]}
            radius={Math.max(3, Math.min(12, Math.sqrt(g.nameplate_capacity_mw || 1) * 2))}
            pane="generatorPane"
            pathOptions={{
              fillColor: getTechColor(g.technology),
              fillOpacity: planned ? 0.35 : 0.7,
              weight: 1,
              color: "#334155",
              dashArray: planned ? "4 4" : undefined,
              pane: "generatorPane",
            }}
          >
            <Popup>
              <div className="text-sm">
                <p className="font-semibold">{g.plant_name}</p>
                <p className="text-gray-400">
                  {g.technology} &bull; {g.nameplate_capacity_mw?.toFixed(1)} MW
                </p>
                <p className="text-gray-500">
                  {g.ba_code} &bull; {g.state_id}
                  {g.status && ` \u2022 ${STATUS_LABELS[g.status] || g.status}`}
                </p>
                <a
                  href={`/generators/${g.plant_id}/${g.generator_id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/generators/${g.plant_id}/${g.generator_id}`);
                  }}
                  className="text-blue-400 hover:underline mt-1 inline-block cursor-pointer"
                >
                  View Details &rarr;
                </a>
              </div>
            </Popup>
          </CircleMarker>
        );
      })}
    </>
  );
}

// ─── Fit bounds to a specific BA ─────────────────────────────────────

function FitToBounds({
  geoData,
  baCode,
  extractBaCode,
}: {
  geoData: GeoJSON.FeatureCollection;
  baCode: string;
  extractBaCode: (zoneNameOrFeature: string | GeoJSON.Feature) => string;
}) {
  const map = useMap();
  const fittedRef = useRef<string | null>(null);

  useEffect(() => {
    if (fittedRef.current === baCode) return; // already zoomed
    for (const feature of geoData.features) {
      if (extractBaCode(feature) === baCode) {
        const layer = L.geoJSON(feature as any);
        map.fitBounds(layer.getBounds(), { padding: [50, 50], maxZoom: 8 });
        fittedRef.current = baCode;
        break;
      }
    }
  }, [map, geoData, baCode, extractBaCode]);

  return null;
}

// ─── Component ───────────────────────────────────────────────────────

const GridMap: React.FC<GridMapProps> = ({
  snapshotData,
  selectedMetric,
  selectedBaCode,
  onZoneClick,
  onZoneHover,
  pipelineByBA,
  showPipelineOverlay = false,
  generators,
  showGeneratorOverlay = false,
  focusBaCode,
  geojsonUrls,
  codeExtractor,
  mapCenter,
  mapZoom,
}) => {
  const [geoData, setGeoData] = useState<GeoJSON.FeatureCollection | null>(null);
  const geoJsonRef = useRef<L.GeoJSON | null>(null);

  // Load grid zone boundaries (US + optionally EU/UK)
  const urls = geojsonUrls ?? ["/data/us_ba_boundaries.geojson"];
  useEffect(() => {
    Promise.all(
      urls.map((url) =>
        fetch(url)
          .then((res) => res.json())
          .catch((err) => {
            console.error(`Failed to load boundaries from ${url}:`, err);
            return { type: "FeatureCollection", features: [] } as GeoJSON.FeatureCollection;
          })
      )
    ).then((collections) => {
      // Merge all feature collections into one
      const merged: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: collections.flatMap((c) => c.features || []),
      };
      setGeoData(merged);
    });
  }, [urls.join(",")]);

  // Compute min/max for normalization
  const { minVal, maxVal } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    snapshotData.forEach((region) => {
      const val = region[selectedMetric];
      if (val != null && isFinite(val as number)) {
        const v = val as number;
        if (v < min) min = v;
        if (v > max) max = v;
      }
    });
    if (!isFinite(min)) min = 0;
    if (!isFinite(max)) max = 1;
    if (max === min) max = min + 1;
    return { minVal: min, maxVal: max };
  }, [snapshotData, selectedMetric]);

  // Extract region code from GeoJSON feature or zoneName string
  const extractBaCode = useCallback((zoneNameOrFeature: string | GeoJSON.Feature, feature?: GeoJSON.Feature): string => {
    // If first arg is a Feature object (from style/onEachFeature callbacks)
    const feat = typeof zoneNameOrFeature === "object" ? zoneNameOrFeature : feature;
    const zoneName = typeof zoneNameOrFeature === "string"
      ? zoneNameOrFeature
      : (zoneNameOrFeature?.properties?.zoneName || "");

    if (codeExtractor && feat) {
      return codeExtractor(feat);
    }

    // EU/UK zones: zoneName IS the code (e.g., "DE", "GB", "IT-NO")
    if (zoneName && !zoneName.startsWith("US-")) {
      return zoneName;
    }

    // US: parse "US-SW-SRP" → "SRP"
    return zoneName.split("-").pop() || zoneName;
  }, [codeExtractor]);

  // Style each polygon
  const style = useCallback(
    (feature: GeoJSON.Feature | undefined): L.PathOptions => {
      if (!feature) return { fillOpacity: 0.05 };
      const baCode = extractBaCode(feature);
      const isSelected = baCode === selectedBaCode;
      const region = snapshotData.get(baCode);

      if (!region) {
        return {
          fillColor: "#334155",
          weight: isSelected ? 2.5 : 1,
          opacity: 0.8,
          color: isSelected ? "#00e5ff" : "#475569",
          fillOpacity: isSelected ? 0.5 : 0.25,
        };
      }

      const val = region[selectedMetric];
      if (val == null) {
        // Has region data but this metric is null — show as dim outline
        return {
          fillColor: "#334155",
          weight: isSelected ? 2.5 : 1,
          opacity: 0.8,
          color: isSelected ? "#00e5ff" : "#475569",
          fillOpacity: isSelected ? 0.5 : 0.25,
        };
      }

      const t = ((val as number) - minVal) / (maxVal - minVal);
      const fillColor = colorForValue(t);

      return {
        fillColor,
        weight: isSelected ? 2.5 : 1,
        opacity: isSelected ? 1 : 0.8,
        color: isSelected ? "#00e5ff" : "#334155",
        fillOpacity: isSelected ? 0.75 : 0.6,
      };
    },
    [snapshotData, selectedMetric, selectedBaCode, minVal, maxVal, extractBaCode]
  );

  // Keep latest callbacks in refs so onEachFeature handlers don't go stale
  const onZoneClickRef = useRef(onZoneClick);
  onZoneClickRef.current = onZoneClick;
  const onZoneHoverRef = useRef(onZoneHover);
  onZoneHoverRef.current = onZoneHover;
  const styleRef = useRef(style);
  styleRef.current = style;

  // Interaction handlers — bound once at mount via refs
  const onEachFeature = useCallback(
    (feature: GeoJSON.Feature, layer: L.Layer) => {
      const baCode = extractBaCode(feature);

      // Tooltip (updated in-place via useEffect below)
      layer.bindTooltip(`<b>${baCode}</b>`, {
        sticky: true,
        className: "grid-map-tooltip",
      });

      // Events — use refs so handlers always call current callbacks
      (layer as L.Path).on({
        click: () => onZoneClickRef.current(baCode),
        mouseover: (e) => {
          onZoneHoverRef.current?.(baCode);
          const path = e.target as L.Path;
          path.setStyle({ fillOpacity: 0.8, weight: 2 });
          path.bringToFront();
        },
        mouseout: (e) => {
          onZoneHoverRef.current?.(null);
          // Reset to current style via ref
          const path = e.target as L.Path;
          const feat = (path as any).feature as GeoJSON.Feature;
          path.setStyle(styleRef.current(feat));
        },
      });
    },
    [extractBaCode]
  );

  // Update styles + tooltips in-place when data/metric/selection changes
  // Use a ref to access current style function without adding it as a dependency
  // (prevents re-render loops from callback reference changes)
  const currentStyleRef = useRef(style);
  currentStyleRef.current = style;

  useEffect(() => {
    if (!geoJsonRef.current) return;
    geoJsonRef.current.eachLayer((layer) => {
      const feat = (layer as any).feature as GeoJSON.Feature | undefined;
      if (!feat) return;
      const baCode = extractBaCode(feat);
      const region = snapshotData.get(baCode);
      const metricVal = region?.[selectedMetric];
      const formatted = metricVal != null ? (metricVal as number).toExponential(2) : "N/A";
      (layer as L.Path).setStyle(currentStyleRef.current(feat));
      layer.setTooltipContent(`<b>${baCode}</b><br/>${formatted}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotData, selectedMetric, selectedBaCode]);

  // GeoJSON key — only changes when boundaries load (mount once)
  const geoKey = useMemo(
    () => `ba-boundaries-${geoData?.features.length ?? 0}`,
    [geoData]
  );

  return (
    <MapContainer
      center={mapCenter ?? [39.8283, -98.5795]}
      zoom={mapZoom ?? 4}
      minZoom={3}
      maxZoom={18}
      zoomSnap={0.5}
      zoomDelta={0.5}
      wheelPxPerZoomLevel={120}
      preferCanvas={true}
      className="h-full w-full"
      style={{ background: "#0A1628" }}
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        keepBuffer={2}
        updateWhenZooming={false}
      />
      {geoData && (
        <GeoJSON
          key={geoKey}
          ref={(ref) => { geoJsonRef.current = ref; }}
          data={geoData}
          style={style}
          onEachFeature={onEachFeature}
        />
      )}
      {showPipelineOverlay && geoData && pipelineByBA && pipelineByBA.size > 0 && (
        <PipelineOverlay
          geoData={geoData}
          pipelineByBA={pipelineByBA}
          extractBaCode={extractBaCode}
          onZoneClick={onZoneClick}
        />
      )}
      {showGeneratorOverlay && generators && generators.length > 0 && (
        <GeneratorOverlay generators={generators} />
      )}
      {focusBaCode && geoData && (
        <FitToBounds geoData={geoData} baCode={focusBaCode} extractBaCode={extractBaCode} />
      )}
      {showGeneratorOverlay && <GeneratorPane />}
    </MapContainer>
  );
};

export default GridMap;
